import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Glyph from 'component/utils/Glyph'
import { isEmpty } from 'utils/validations'

const Field = ({
  error,
  input,
  glyph,
  glyphClickable = false,
  hint,
  validate,
  defaultActive,
  children,
  select,
  onMouseDown = () => {},
  onMouseUp = () => {},
  onClick = () => {},
  className,
  checkbox,
  radio,
  disabledHelper,
  ignoreActiveClass = false,
}) => {
  /*
   * This will return the field class according to the following conditions:
   * - default: .field
   * - in case there is an icon: .field addon-right
   * - in case it has any value: .field active
   * - in case it has any value and no errors: .field active valid
   * - in case there is an error: .field error ou .field active error
   */
  const fieldClass = classNames(
    'field',
    {
      'addon-right': glyph,
      active: !ignoreActiveClass && (!isEmpty(input.value) || defaultActive),
      valid:
        validate &&
        input.value &&
        (Array.isArray(input.value) ? input.value.length : true) &&
        !error,
      invalid: validate && error,
      [className]: className,
    },
    className,
    { 'field-select': select },
    { 'field-checkbox': checkbox },
    { 'field-radio': radio }
  )

  const fieldSelect = classNames('field-select')

  /*
   *  renders the field helper:
   * - in case there is an error, it will be rendered.
   * - in case there is an suggestion, it will be rendered.
   */
  const renderHint = () => {
    if (!disabledHelper) {
      if (error) {
        return <span className='helper'>{error}</span>
      }

      return <span className='helper'>{hint}</span>
    }
  }

  /*
   * Renders a glyph when there is one
   */
  const renderGlyph = () => {
    if (!glyph) {
      return
    }

    return (
      <div
        className='addon right'
        style={
          glyphClickable ? { cursor: 'pointer', pointerEvents: 'visible' } : {}
        }
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
      >
        <Glyph type={glyph} />
      </div>
    )
  }

  const handleMouseDown = (event) => {
    if (onMouseDown) {
      onMouseDown(event)
    }
  }

  const handleMouseUp = (event) => {
    if (onMouseUp) {
      onMouseUp(event)
    }
  }

  const handleClick = (event) => {
    if (onClick) {
      onClick(event)
    }
  }

  return (
    <div className={select ? `${fieldClass} ${fieldSelect}` : fieldClass}>
      {children}

      {renderGlyph()}

      {renderHint()}
    </div>
  )
}

Field.propTypes = {
  checkbox: PropTypes.bool,
  className: PropTypes.string,
  input: PropTypes.object.isRequired,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  glyph: PropTypes.string,
  glyphClickable: PropTypes.bool,
  hint: PropTypes.string,
  validate: PropTypes.bool,
  defaultActive: PropTypes.bool,
  children: PropTypes.any.isRequired,
  radio: PropTypes.bool,
  select: PropTypes.bool,
  disabledHelper: PropTypes.bool,
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  onClick: PropTypes.func,
  ignoreActiveClass: PropTypes.bool,
}

export default Field

export { Field }
